import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import FalconComponentCard from 'components/common/FalconComponentCard';
import { getSize } from 'helpers/utils';
import Flex from 'components/common/Flex';
import { useDropzone } from 'react-dropzone';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import CardDropdown from 'components/common/CardDropdown';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';

const FileUploader = ({ category, setFileUrl }) => {
  const [files, setFiles] = useState([]);

  const [loading, setLoading] = useState(false);

  const schema = yup
    .object({
      uploadedFiles: yup.array().required('This field is required.'),
    })
    .required();

  const handleRemove = (path) => {
    const filteredFiles = files.filter((file) => file.path !== path);
    setFiles(filteredFiles);
    setValue('uploadedFiles', filteredFiles.length ? filteredFiles : null);
    setFileUrl(null);
  };

  const {
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });

  let accepted = {};

  if (category === 'banners' || category === 'news') {
    accepted = {
      'image/*': ['.jpeg', '.png', '.jpg'],
    };
  } else {
    accepted = {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.svg', '.webp'],
    };
  }

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: accepted,
    onDrop: (acceptedFiles) => {
      if (errors.uploadedFiles) {
        clearErrors('uploadedFiles');
      }
      setValue('uploadedFiles', acceptedFiles);
      setFiles([
        // ...files,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);

      acceptedFiles.forEach(async (file) => {
        setLoading(true);
        
        try {
          // Get SAS token from backend
          const sasResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/get-sas-token?category=${category}&filename=${file.name}`
          );
          
          // Check response type and parse accordingly
          const contentType = sasResponse.headers.get('content-type');
          let sasTokenOrUrl;
          
          if (contentType && contentType.includes('application/json')) {
            const data = await sasResponse.json();
            sasTokenOrUrl = data.sasToken;
          } else {
            // Backend returns plain text (either SAS token or full URL)
            sasTokenOrUrl = await sasResponse.text();
          }
          
          console.log('SAS Token/URL received:', sasTokenOrUrl);
          
          // Check if backend returned full URL or just SAS token
          let blobUrl;
          if (sasTokenOrUrl.startsWith('http')) {
            // Backend returned full URL with SAS token
            blobUrl = sasTokenOrUrl;
          } else {
            // Backend returned just SAS token, construct full URL
            const containerName = (category === 'banners' || category === 'news') ? 'images' : 'documents';
            blobUrl = `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT}.blob.core.windows.net/${containerName}/${category}/${file.name}${sasTokenOrUrl}`;
          }
          
          console.log('Uploading to:', blobUrl);
          
          // Upload to Azure Blob Storage
          fetch(blobUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'Content-Type': file.type
            }
          })
            .then((response) => {
              if (response.ok) {
                // Extract the URL without SAS token for display
                const urlWithoutSas = blobUrl.split('?')[0];
                setFileUrl(urlWithoutSas);
                console.log('Upload successful:', urlWithoutSas);
              } else {
                console.error('Upload failed:', response.status, response.statusText);
              }
            })
            .catch((error) => {
              console.error('Upload error:', error);
            })
            .finally(() => {
              console.log('finally');
              setLoading(false);
            });
            
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      });
    },
  });

  return (
    <>
      <FalconComponentCard>
        <FalconComponentCard.Body>
          <div {...getRootProps({ className: 'dropzone-area py-6' })}>
            <input {...getInputProps({ multiple: false })} />
            <Flex justifyContent='center'>
              <img src={cloudUpload} alt='' width={25} className='me-2' />
              {loading ? (
                <p className='fs-0 mb-0 text-700'>Uploading...</p>
              ) : (
                <p className='fs-0 mb-0 text-700'>Drop the file here.</p>
              )}
            </Flex>
          </div>
          <div className='invalid-feedback'>
            {errors.uploadedFiles?.message}
          </div>
          <div>
            {files.map((file) => (
              <Flex
                alignItems='center'
                className='py-3 border-bottom btn-reveal-trigger'
                key={file.path}
              >
                {(category === 'banners' || category === 'news') && (
                  <img
                    className='rounded'
                    width={100}
                    src={file.preview}
                    alt={file.path}
                  />
                )}

                <Flex
                  justifyContent='between'
                  alignItems='center'
                  className='ms-3 flex-1'
                >
                  <div>
                    <h6>{file.path}</h6>
                    <Flex className='position-relative' alignItems='center'>
                      <p className='mb-0 fs--1 text-400 line-height-1'>
                        <strong>{getSize(file.size)}</strong>
                      </p>
                    </Flex>
                  </div>
                </Flex>
                <CardDropdown>
                  <div className='py-2'>
                    <Dropdown.Item
                      className='text-danger'
                      onClick={() => handleRemove(file.path)}
                    >
                      Remove
                    </Dropdown.Item>
                  </div>
                </CardDropdown>
              </Flex>
            ))}
          </div>
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default FileUploader;

// const FileUploadValidation = () => {
//   const schema = yup
//     .object({
//       uploadedFiles: yup.array().required('This field is required.'),
//     })
//     .required();
//   const {
//     handleSubmit,
//     setValue,
//     formState: { errors },
//     clearErrors,
//   } = useForm({
//     resolver: yupResolver(schema),
//   });
//   const [files, setFiles] = useState([]);

//   const { getRootProps, getInputProps } = useDropzone({
//     accept: {
//       'image/*': ['.jpeg', '.png', '.jpg'],
//     },
//     onDrop: (acceptedFiles) => {
//       if (errors.uploadedFiles) {
//         clearErrors('uploadedFiles');
//       }
//       setValue('uploadedFiles', acceptedFiles);
//       setFiles([
//         ...files,
//         ...acceptedFiles.map((file) =>
//           Object.assign(file, {
//             preview: URL.createObjectURL(file),
//           })
//         ),
//       ]);
//     },
//   });

//   const handleRemove = (path) => {
//     const filteredFiles = files.filter((file) => file.path !== path);
//     setFiles(filteredFiles);
//     setValue('uploadedFiles', filteredFiles.length ? filteredFiles : null);
//   };

//   const onSubmit = (data) => {
//     console.log(data);
//   };

//   return (
//     <Form onSubmit={handleSubmit(onSubmit)}>
//       <div
//         {...getRootProps({
//           className: `${classNames('dropzone-area py-6', {
//             'is-invalid': errors.uploadedFiles,
//           })}`,
//         })}
//       >
//         <input name='uploadedFiles' {...getInputProps()} />
//         <Flex justifyContent='center'>
//           <img src={cloudUpload} alt='' width={25} className='me-2' />
//           <p className='fs-0 mb-0 text-700'>Drop your images here</p>
//         </Flex>
//       </div>
//       <div className='invalid-feedback'>{errors.uploadedFiles?.message}</div>
//       <div>
//         {files.map((file) => (
//           <Flex
//             alignItems='center'
//             className='py-3 border-bottom btn-reveal-trigger'
//             key={file.path}
//           >
//             <img
//               className='rounded'
//               width={40}
//               height={40}
//               src={file.preview}
//               alt={file.path}
//             />

//             <Flex
//               justifyContent='between'
//               alignItems='center'
//               className='ms-3 flex-1'
//             >
//               <div>
//                 <h6>{file.path}</h6>
//                 <Flex className='position-relative' alignItems='center'>
//                   <p className='mb-0 fs--1 text-400 line-height-1'>
//                     <strong>{getSize(file.size)}</strong>
//                   </p>
//                 </Flex>
//               </div>
//             </Flex>
//             <CardDropdown>
//               <div className='py-2'>
//                 <Dropdown.Item
//                   className='text-danger'
//                   onClick={() => handleRemove(file.path)}
//                 >
//                   Remove
//                 </Dropdown.Item>
//               </div>
//             </CardDropdown>
//           </Flex>
//         ))}
//       </div>
//       <Button variant='primary' type='submit' className='mt-3' size='sm'>
//         Submit
//       </Button>
//     </Form>
//   );
// };
